import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useHttpClient } from '@bonusly/http-client';

import { BizyEverboarding } from '../schemas/bizy-everboarding';
import { headers } from './meta';

// ─── Query Keys ──────────────────────────────────────────────────────────────

enum QueryKey {
	Everboarding = 'bizy-everboarding',
}

export const bizyEverboardingQueryKey = () => [QueryKey.Everboarding];

// ─── Path helpers ────────────────────────────────────────────────────────────

export const bizyEverboardingPath = (prefix = '') => `${prefix}/bizy/everboarding`;

// ─── Query hooks ─────────────────────────────────────────────────────────────

/**
 * Fetches the current user's everboarding status and timestamps.
 *
 * GET /api/v2/bizy/everboarding
 */
export const useBizyEverboarding = (enabled = true) => {
	const http = useHttpClient();

	return useQuery({
		queryKey: bizyEverboardingQueryKey(),
		queryFn: async () => {
			const res = await http.get<Record<string, unknown>>(bizyEverboardingPath(), {
				headers,
			});

			return BizyEverboarding.parse(res.data);
		},
		enabled,
		refetchOnMount: 'always',
	});
};

// ─── Mutation hooks ──────────────────────────────────────────────────────────

/**
 * Resets (deletes) the current user's everboarding record.
 *
 * DELETE /api/v2/bizy/everboarding
 */
export const useBizyResetEverboardingMutation = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (): Promise<void> => {
			await http.delete(bizyEverboardingPath());
		},
		onSuccess: () => {
			queryClient.invalidateQueries(bizyEverboardingQueryKey());
		},
	});
};
